import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell, Package, Truck, RotateCcw, Receipt, Wallet, LifeBuoy, ShieldCheck,
  BadgePercent, Megaphone, Star, CheckCheck, Inbox,
} from "lucide-react";
import api from "@/lib/api";

const KIND_ICON = {
  order: Package, shipping: Truck, return: RotateCcw, refund: Receipt, payment: Wallet,
  settlement: Wallet, support: LifeBuoy, seller_verification: ShieldCheck,
  promotion: BadgePercent, marketing: Megaphone, review: Star,
};
const LEVEL_TINT = {
  urgent: "bg-rose-50 text-rose-500", warning: "bg-amber-50 text-amber-500",
  success: "bg-emerald-50 text-emerald-500", info: "bg-blue-50 text-blue-500",
};

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function NotificationBell({ viewAllPath = "/app/notifications" }) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState(null);
  const navigate = useNavigate();
  const ref = useRef(null);

  const loadCount = useCallback(() => {
    api.get("/notifications/unread-count").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
  }, []);
  const loadItems = useCallback(() => {
    api.get("/notifications/inbox?limit=8").then((r) => { setItems(r.data.items || []); setUnread(r.data.unread || 0); }).catch(() => setItems([]));
  }, []);

  useEffect(() => { loadCount(); const id = setInterval(loadCount, 45000); return () => clearInterval(id); }, [loadCount]);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = () => { const next = !open; setOpen(next); if (next) loadItems(); };

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) { try { await api.post("/notifications/mark-read", { id: n.id }); setUnread((u) => Math.max(0, u - 1)); } catch (e) {} }
    if (n.route) navigate(n.route);
  };
  const readAll = async () => {
    try { await api.post("/notifications/mark-all-read"); setUnread(0); loadItems(); } catch (e) {}
  };

  return (
    <div className="relative shrink-0" ref={ref}>
      <button className="relative rounded-full p-2 hover:bg-slate-100" data-testid="notifications-btn" onClick={toggle} aria-label="Notifications">
        <Bell className="h-5 w-5 text-slate-600" />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-none text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            className="absolute right-0 z-50 mt-2 w-[360px] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl" data-testid="notif-dropdown">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <p className="text-sm font-semibold text-slate-800">Notifications</p>
              {unread > 0 && (
                <button onClick={readAll} data-testid="notif-dropdown-read-all" className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700">
                  <CheckCheck className="h-3.5 w-3.5" /> Mark all read
                </button>
              )}
            </div>
            <div className="max-h-[380px] overflow-y-auto">
              {items === null ? (
                <div className="space-y-2 p-3">{[0, 1, 2].map((i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-slate-100" />)}</div>
              ) : items.length === 0 ? (
                <div className="flex flex-col items-center gap-2 px-4 py-10 text-center" data-testid="notif-dropdown-empty">
                  <Inbox className="h-8 w-8 text-slate-300" />
                  <p className="text-sm text-slate-500">You're all caught up</p>
                </div>
              ) : (
                items.map((n) => {
                  const Icon = KIND_ICON[n.kind] || Bell;
                  const tint = LEVEL_TINT[n.level] || LEVEL_TINT.info;
                  return (
                    <button key={n.id} onClick={() => openItem(n)} data-testid="notif-dropdown-item"
                      className={`flex w-full items-start gap-3 border-b border-slate-50 px-4 py-3 text-left transition hover:bg-slate-50 ${n.read ? "opacity-60" : ""}`}>
                      <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${tint}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-800">{n.title}</p>
                        {n.subtitle && <p className="truncate text-xs text-slate-500">{n.subtitle}</p>}
                        <p className="mt-0.5 text-[11px] text-slate-400">{timeAgo(n.created_at)}</p>
                      </div>
                      {!n.read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-blue-500" />}
                    </button>
                  );
                })
              )}
            </div>
            <button onClick={() => { setOpen(false); navigate(viewAllPath); }} data-testid="notif-view-all"
              className="block w-full border-t border-slate-100 py-3 text-center text-sm font-medium text-blue-600 hover:bg-slate-50">
              View all notifications
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default NotificationBell;
